import { z } from "zod";

export const CAPABILITY_TOKEN_ISSUER = "glider-platform-api";
export const CAPABILITY_TOKEN_AUDIENCE_EXECUTION_ENGINE =
  "glider-execution-engine";
export const CAPABILITY_TOKEN_TYPE = "glider-capability-v1";

export const CAPABILITY_TOKEN_SCOPES = [
  "execute:trade",
  "execute:bridge",
  "execute:transfer",
  "execute:withdraw",
  "execute:rebalance",
  "execute:deposit",
  "feed:read",
  "quote:read",
] as const;

export type CapabilityScope = (typeof CAPABILITY_TOKEN_SCOPES)[number];

export type CapabilityTokenAlgorithm = "HS256";

const capabilityScopeSchema = z.enum(CAPABILITY_TOKEN_SCOPES);

const portfolioResourceSchema = z
  .string()
  .regex(/^portfolio:[^:*\s]+$/u)
  .refine((resource) => resource !== "portfolio:*");

const capabilityGrantSchema = z.object({
  resource: portfolioResourceSchema,
  scopes: z
    .array(capabilityScopeSchema)
    .min(1)
    .refine((scopes) => new Set(scopes).size === scopes.length),
});

export const capabilityTokenClaimsSchema = z.object({
  iss: z.literal(CAPABILITY_TOKEN_ISSUER),
  aud: z.string().min(1),
  typ: z.literal(CAPABILITY_TOKEN_TYPE),
  sub: z.string().min(1),
  act: z
    .object({
      type: z.enum([
        "user",
        "tenant",
        "agent",
        "addon",
        "service",
        "operator",
      ]),
      id: z.string().min(1),
    })
    .optional(),
  ownerAddress: z.string().regex(/^0x[a-fA-F0-9]{40}$/u).optional(),
  tenantId: z.string().min(1).optional(),
  grants: z.array(capabilityGrantSchema).min(1),
  sessionRef: z.string().min(1).optional(),
  jti: z.string().min(1),
  iat: z.number().int().nonnegative(),
  exp: z.number().int().positive(),
});

export type CapabilityGrant = z.infer<typeof capabilityGrantSchema>;
export type CapabilityTokenClaims = z.infer<typeof capabilityTokenClaimsSchema>;

export type MintCapabilityTokenInput = {
  readonly claims: CapabilityTokenClaims;
  readonly kid: string;
  readonly secret: string;
};

export type VerifyCapabilityTokenInput = {
  readonly allowedAlgs?: readonly string[];
  readonly audience: string;
  readonly now?: Date;
  readonly secretsByKid: Readonly<Record<string, string>>;
  readonly token: string;
};

export type VerifyCapabilityTokenError =
  | "expired"
  | "bad_signature"
  | "unknown_kid"
  | "wrong_audience"
  | "wrong_typ"
  | "malformed"
  | "not_yet_valid";

export type VerifyCapabilityTokenResult =
  | {
      readonly ok: true;
      readonly claims: CapabilityTokenClaims;
    }
  | {
      readonly ok: false;
      readonly error: VerifyCapabilityTokenError;
    };

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

const normalizeRequiredString = (value: string, label: string): string => {
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    throw new Error(`${label} is required.`);
  }

  return trimmed;
};

const bytesToBase64Url = (bytes: Uint8Array): string => {
  let binary = "";
  for (let index = 0; index < bytes.length; index += 0x8000) {
    const chunk = bytes.subarray(index, index + 0x8000);
    binary += String.fromCharCode(...chunk);
  }

  return btoa(binary)
    .replace(/\+/gu, "-")
    .replace(/\//gu, "_")
    .replace(/=+$/u, "");
};

const base64UrlToBytes = (value: string): Uint8Array => {
  if (!/^[A-Za-z0-9_-]*$/u.test(value)) {
    throw new Error("Invalid base64url segment.");
  }

  const normalized = value.replace(/-/gu, "+").replace(/_/gu, "/");
  const padded = normalized.padEnd(
    normalized.length + ((4 - (normalized.length % 4)) % 4),
    "=",
  );
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return bytes;
};

const encodeJsonSegment = (value: unknown): string =>
  bytesToBase64Url(textEncoder.encode(JSON.stringify(value)));

const decodeJsonSegment = (value: string): unknown =>
  JSON.parse(textDecoder.decode(base64UrlToBytes(value))) as unknown;

const toArrayBuffer = (bytes: Uint8Array): ArrayBuffer => {
  const buffer = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(buffer).set(bytes);
  return buffer;
};

const importHmacKey = async (secret: string): Promise<CryptoKey> =>
  await crypto.subtle.importKey(
    "raw",
    textEncoder.encode(normalizeRequiredString(secret, "Capability token secret")),
    {
      hash: "SHA-256",
      name: "HMAC",
    },
    false,
    ["sign", "verify"],
  );

const signPayload = async (
  payload: string,
  secret: string,
): Promise<string> => {
  const key = await importHmacKey(secret);
  const signature = await crypto.subtle.sign(
    "HMAC",
    key,
    textEncoder.encode(payload),
  );
  return bytesToBase64Url(new Uint8Array(signature));
};

const verifySignature = async (params: {
  readonly payload: string;
  readonly secret: string;
  readonly signature: string;
}): Promise<boolean> => {
  const key = await importHmacKey(params.secret);
  return await crypto.subtle.verify(
    "HMAC",
    key,
    toArrayBuffer(base64UrlToBytes(params.signature)),
    textEncoder.encode(params.payload),
  );
};

const jwtHeaderSchema = z.object({
  alg: z.string().min(1),
  typ: z.literal("JWT"),
  kid: z.string().min(1),
});

const partialClaimsSchema = z.object({
  aud: z.unknown(),
  typ: z.unknown(),
});

const readOwnSecretForKid = (
  secretsByKid: Readonly<Record<string, string>>,
  kid: string,
): string | null => {
  if (!Object.hasOwn(secretsByKid, kid)) {
    return null;
  }

  const secret = secretsByKid[kid];
  if (typeof secret !== "string") {
    return null;
  }

  const trimmed = secret.trim();
  return trimmed.length > 0 ? trimmed : null;
};

export async function mintCapabilityToken(
  input: MintCapabilityTokenInput,
): Promise<string> {
  const claims = capabilityTokenClaimsSchema.parse(input.claims);
  const kid = normalizeRequiredString(input.kid, "Capability token kid");
  const header = {
    alg: "HS256",
    typ: "JWT",
    kid,
  };
  const payload = `${encodeJsonSegment(header)}.${encodeJsonSegment(claims)}`;
  const signature = await signPayload(payload, input.secret);

  return `${payload}.${signature}`;
}

export async function verifyCapabilityToken(
  input: VerifyCapabilityTokenInput,
): Promise<VerifyCapabilityTokenResult> {
  const [encodedHeader, encodedClaims, signature, extra] =
    input.token.split(".");
  if (!encodedHeader || !encodedClaims || !signature || extra !== undefined) {
    return { ok: false, error: "malformed" };
  }

  let header: z.infer<typeof jwtHeaderSchema>;
  try {
    header = jwtHeaderSchema.parse(decodeJsonSegment(encodedHeader));
  } catch {
    return { ok: false, error: "malformed" };
  }

  const allowedAlgs = input.allowedAlgs ?? ["HS256"];
  if (!allowedAlgs.includes(header.alg) || header.alg !== "HS256") {
    return { ok: false, error: "malformed" };
  }

  const secret = readOwnSecretForKid(input.secretsByKid, header.kid);
  if (!secret) {
    return { ok: false, error: "unknown_kid" };
  }

  const payload = `${encodedHeader}.${encodedClaims}`;
  let signatureValid = false;
  try {
    signatureValid = await verifySignature({
      payload,
      secret,
      signature,
    });
  } catch {
    return { ok: false, error: "bad_signature" };
  }
  if (!signatureValid) {
    return { ok: false, error: "bad_signature" };
  }

  let decodedClaims: unknown;
  try {
    decodedClaims = decodeJsonSegment(encodedClaims);
  } catch {
    return { ok: false, error: "malformed" };
  }

  const partialClaims = partialClaimsSchema.safeParse(decodedClaims);
  if (!partialClaims.success) {
    return { ok: false, error: "malformed" };
  }

  if (partialClaims.data.typ !== CAPABILITY_TOKEN_TYPE) {
    return { ok: false, error: "wrong_typ" };
  }

  if (partialClaims.data.aud !== input.audience) {
    return { ok: false, error: "wrong_audience" };
  }

  const parsedClaims = capabilityTokenClaimsSchema.safeParse(decodedClaims);
  if (!parsedClaims.success) {
    return { ok: false, error: "malformed" };
  }

  const claims = parsedClaims.data;
  const nowSeconds = Math.floor((input.now ?? new Date()).getTime() / 1000);
  if (nowSeconds >= claims.exp) {
    return { ok: false, error: "expired" };
  }
  if (claims.iat > nowSeconds + 60) {
    return { ok: false, error: "not_yet_valid" };
  }

  return { ok: true, claims };
}

export function capabilityGrantsCover(params: {
  readonly claims: CapabilityTokenClaims;
  readonly portfolioIds: readonly string[];
  readonly scope: CapabilityScope;
}): boolean {
  const requestedPortfolioIds = Array.from(
    new Set(
      params.portfolioIds
        .map((portfolioId) => portfolioId.trim())
        .filter((portfolioId) => portfolioId.length > 0),
    ),
  );

  if (requestedPortfolioIds.length === 0) {
    return true;
  }

  const scopesByResource = new Map<string, ReadonlySet<CapabilityScope>>();
  for (const grant of params.claims.grants) {
    scopesByResource.set(grant.resource, new Set(grant.scopes));
  }

  return requestedPortfolioIds.every((portfolioId) =>
    scopesByResource.get(`portfolio:${portfolioId}`)?.has(params.scope) === true,
  );
}

export function normalizeCapabilityTokenSecrets(
  raw: string | null | undefined,
): Record<string, string> | null {
  const trimmed = raw?.trim();
  if (!trimmed) {
    return null;
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed) as unknown;
  } catch {
    return null;
  }

  const objectResult = z.record(z.string(), z.string()).safeParse(parsed);
  if (!objectResult.success) {
    return null;
  }

  const entries = Object.entries(objectResult.data)
    .map(([kid, secret]) => [kid.trim(), secret.trim()] as const)
    .filter(([kid, secret]) => kid.length > 0 && secret.length > 0);

  return entries.length > 0 ? Object.fromEntries(entries) : null;
}
